/* One driver's record: by day, by month, by year.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * ONE SCREEN, TWO READERS, AND THE SERVER DECIDES WHICH.
 *
 * A courier opens their own and dispatch opens anybody's. The difference is
 * the route it asks for, never a flag on this page: /drivers/me takes the
 * username off the session and there is no parameter that changes it, while
 * /drivers/:username is gated on the project admin role. A page that chose
 * between them from a prop would be one mistaken prop away from a courier
 * reading where every other courier went, which is a list of patient
 * addresses wearing a payslip.
 *
 * So the route is chosen from the URL this page was mounted at, and if the
 * reader is not entitled to it the server answers 403 and this renders that.
 *
 * WHAT IT IS FOR. Daily, monthly and yearly totals, so a payment run can be
 * checked and disbursed. The pharmacies each period's work was for, because
 * "delivered for" is part of the question. No patient and no address, because
 * the server does not send them and a pay record should not carry them.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { Loading } from '../../app/Loading';
import { Section } from '../../app/Section';
import { Pager, usePaged } from '../../app/Pager';
import { useAuth, useProjectTimezone } from '../../app/auth';
import { daysBeforeIn, todayIn } from '../../lib/when';
import { money } from './Drivers';
import { handleUnder } from '../../lib/people';
import { CardLink, Chip, Context, Panel, Screen, ScreenHeader, Sheet } from '../../app/Glass';
import { ReturnIcon } from '../../app/Icons';

interface PayLine { label: string; delivered: number; rateCents: number; payCents: number | null }

interface Totals {
    delivered: number;
    failed: number;
    payCents: number | null;
    rateSet: boolean;
    /** What the lines are keyed on, so the column can be headed honestly. */
    basis: 'serviceType' | 'zone';
    lines: Record<string, PayLine>;
}

interface Period extends Totals {
    period: string;
    pharmacies: Array<{ name: string; delivered: number; failed: number }>;
}

interface Record_ {
    from: string; to: string; timezone: string; currency: string;
    username: string; name: string;
    grouping: 'day' | 'month' | 'year';
    periods: Period[];
    totals: Totals;
}

const GROUPINGS = [
    { value: 'day', label: 'Day' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

/** `mine` is set by the route, not by a prop a caller could get wrong. */
export function DriverRecord({ mine = false }: { mine?: boolean }) {
    const { code = '', username = '' } = useParams();
    const { projects, user } = useAuth();
    const project = projects.find((p) => p.code === code);
    const [params, setParams] = useSearchParams();

    /* In the project's zone at both ends: this is the window a driver
       checks their own pay against. See lib/when.ts. */
    const timezone = useProjectTimezone(code);
    const from = params.get('from') ?? daysBeforeIn(timezone, 30);
    const to = params.get('to') ?? todayIn(timezone);
    const groupBy = params.get('groupBy') ?? 'day';

    const [data, setData] = useState<Record_ | null>(null);
    /* Grouped by day over a quarter this is ninety panels. The totals
       above it are the whole range whatever page you are on, so the
       figure a driver came to check is never the thing that got paged. */
    const periods = usePaged(data?.periods ?? []);
    const [msg, setMsg] = useState<string | null>(null);

    /* The route is the authorization, so it is built here from which page
       this is rather than from anything the reader can influence. */
    const who = mine ? 'me' : encodeURIComponent(username);

    const load = useCallback(async () => {
        setMsg(null);
        try {
            setData(await api<Record_>(
                `/api/projects/${code}/uh/drivers/${who}?from=${from}&to=${to}&groupBy=${groupBy}`,
            ));
        } catch (err) {
            setMsg(err instanceof ApiError ? err.message : 'Could not load the record.');
        }
    }, [code, who, from, to, groupBy]);
    useEffect(() => { void load(); }, [load]);

    const set = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value === '') next.delete(key); else next.set(key, value);
        setParams(next, { replace: true });
    };

    if (!project) {
        return (<><h1>Not available</h1><Link className="izy-btn secondary" to="/">Back</Link></>);
    }
    if (data === null) {
        return msg
            ? (<><h1>Deliveries</h1><div className="izy-alert error" role="alert">{msg}</div></>)
            : <div className="izy-card"><Loading label="Loading the record" /></div>;
    }

    const title = mine ? 'Your deliveries' : data.name;

    /* ONE SCREEN, TWO SHAPES. `mine` is the courier's own record — the
       History tab of the phone app — and it gets the app's frame. Without
       it this is a site manager looking somebody up at a desk, which is a
       console page and should stay one. Same split as Discrepancies, and
       the same flag that already decides the heading and whether a
       username is printed under it. */
    const page = (
        <>
                {/* The heading above is already the person's name. This line
                    repeated their username, which on this contract is their
                    email address -- a personal address in the subtitle of a
                    page a site manager reads with somebody looking over their
                    shoulder. Shown only when it is a handle rather than an
                    address, and never for your own record, where it told you
                    who you are. See lib/people.ts. */}
            {msg && <div className="izy-alert error" role="alert">{msg}</div>}

            {!mine && (
                <p>
                    <Link className="izy-btn secondary" to={`/projects/${code}/drivers?from=${from}&to=${to}`}>
                        Back to all drivers
                    </Link>
                </p>
            )}

            {/* Said once rather than as a dash in every row. */}
            {!data.totals.rateSet && (
                <div className="izy-alert warn" role="status">
                    No pay is shown because the per-delivery rate is not set for every service level
                    worked in this range. The counts below are complete; the money is waiting on the
                    rate card.
                </div>
            )}

            <div className="izy-card">
                <div className="izy-row">
                    <label className="izy-field">From
                        <input type="date" value={from} onChange={(e) => set('from', e.target.value)} />
                    </label>
                    <label className="izy-field">To
                        <input type="date" value={to} onChange={(e) => set('to', e.target.value)} />
                    </label>
                    <label className="izy-field">Grouped by
                        <select value={groupBy} onChange={(e) => set('groupBy', e.target.value)}>
                            {GROUPINGS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            {/* A courier's own record shows these in the panel above, where
                they stay on screen while the days below scroll. Printing
                them twice is the clutter the Section summary rule exists to
                stop. A manager has no panel, so they stay here. */}
            {!mine && (
            <div className="izy-card">
                <h2>{from} to {to}</h2>
                <div className="izy-stats">
                    <div><b>{data.totals.delivered}</b><span>delivered</span></div>
                    <div className={data.totals.failed > 0 ? 'izy-stat-bad' : undefined}>
                        <b>{data.totals.failed}</b><span>not delivered</span>
                    </div>
                    <div><b>{data.periods.length}</b><span>{groupBy === 'day' ? 'days' : `${groupBy}s`}</span></div>
                    <div><b>{money(data.totals.payCents, data.currency)}</b><span>{mine ? 'earned' : 'to disburse'}</span></div>
                </div>
            </div>
            )}

            {data.periods.length === 0 ? (
                <div className="izy-card"><p className="izy-muted">Nothing delivered in this range.</p></div>
            ) : periods.rows.map((p) => (
                <Section
                    key={p.period}
                    id={`driver-period-${p.period}`}
                    title={p.period}
                    summary={`${p.delivered} delivered${p.failed > 0 ? `, ${p.failed} not` : ''} · ${money(p.payCents, data.currency)}`}
                >
                    <table className="izy-table">
                        <thead><tr><th>Pharmacy</th><th className="izy-num">Delivered</th><th className="izy-num">Not delivered</th></tr></thead>
                        <tbody>
                            {p.pharmacies.map((ph) => (
                                <tr key={ph.name}>
                                    <td>{ph.name}</td>
                                    <td className="izy-num">{ph.delivered}</td>
                                    <td className={`izy-num${ph.failed > 0 ? ' izy-stat-bad' : ''}`}>{ph.failed}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {Object.keys(p.lines).length > 0 && (
                        <table className="izy-table">
                            {/* The heading follows the rate card. A column
                                headed "Service level" with zones under it is
                                a payslip nobody can check, and this project
                                can be set either way. */}
                            <thead><tr><th>{p.basis === 'zone' ? 'Zone' : 'Service level'}</th><th className="izy-num">Delivered</th><th className="izy-num">Rate</th><th className="izy-num">Pay</th></tr></thead>
                            <tbody>
                                {Object.entries(p.lines).map(([key, line]) => (
                                    <tr key={key}>
                                        <td>{line.label}</td>
                                        <td className="izy-num">{line.delivered}</td>
                                        <td className="izy-num">{line.rateCents > 0 ? money(line.rateCents, data.currency) : 'not set'}</td>
                                        <td className="izy-num">{money(line.payCents, data.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Section>
            ))}
            <Pager of={periods} noun={groupBy === 'day' ? 'days' : `${groupBy}s`} />
        </>
    );

    if (mine) {
        return (
            <Screen>
                <Context>
                    <ScreenHeader title="Your deliveries" subtitle={`times in ${data.timezone}`} />
                    {/* The three numbers somebody opens their own record
                        for, in the region that does not scroll away: how
                        many, how many went wrong, and what it comes to. The
                        breakdown by day is the work underneath. */}
                    <Panel>
                        <h2 className="izy-lede-kicker">{from} to {to}</h2>
                        <p className="izy-lede">{money(data.totals.payCents, data.currency)}</p>
                        {/* The word, on its own line under the number. A
                            figure with no caption is the one thing on this
                            screen somebody could read as the wrong total —
                            and it is deliberately "earned" rather than the
                            manager's "to disburse", because this is their
                            own record. */}
                        <p className="izy-lede-note">earned</p>
                        <div className="izy-chips">
                            <Chip label={`${data.totals.delivered} delivered`} tone="good" />
                            {data.totals.failed > 0 && <Chip label={`${data.totals.failed} not delivered`} tone="bad" />}
                            <Chip label={`${data.periods.length} ${groupBy === 'day' ? 'days' : `${groupBy}s`}`} />
                        </div>
                    </Panel>
                </Context>
                <Sheet>
                    {/* DIRECTLY UNDER WHAT THEY EARNED, and that placement is
                        the point rather than a tidy-up. This is the screen a
                        courier opens at the end of a round -- to see what the
                        day came to -- and it is the same moment they are
                        standing at a van with packages that did not go out.
                        Section 9 of the offer letter wants those back at
                        their own pharmacy the same day.

                        Always offered, not only when today's run has a
                        failure on it: a package can sit in a van overnight,
                        and a screen that hides the way to hand it back is how
                        it stays there. */}
                    <CardLink
                        to={`/projects/${code}/returns`}
                        title="Take back undelivered"
                        Icon={ReturnIcon}
                        tone="quiet"
                        compact
                        detail="To the pharmacy it came from"
                    />
                    {page}
                </Sheet>
            </Screen>
        );
    }

    return (
        <>
            <h1>{title}</h1>
            <p className="izy-sub">
                {/* The heading above is already the person's name. This line
                    repeated their username, which on this contract is their
                    email address -- a personal address in the subtitle of a
                    page a site manager reads with somebody looking over their
                    shoulder. Shown only when it is a handle rather than an
                    address. See lib/people.ts. */}
                {handleUnder(data) !== null && <>{handleUnder(data)} · </>}
                times in {data.timezone}
            </p>
            {page}
        </>
    );
}
